import { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Camera, Check, AlertCircle, Loader2, Upload, FileText } from 'lucide-react';
import DocumentScanner from '../components/DocumentScanner';

import { API_URL } from '../utils/api';

export default function CapturePage() {
  const { sessionId } = useParams();
  const [uploading, setUploading] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [error, setError] = useState('');
  const [lastSuccess, setLastSuccess] = useState(false);
  const [scanMode, setScanMode] = useState(false);
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const uploadPhoto = async (file) => {
    setUploading(true);
    setError('');
    setLastSuccess(false);

    const formData = new FormData();
    formData.append('photo', file, file.name || 'photo.jpg');

    try {
      const { data } = await axios.post(
        `${API_URL}/api/photo/upload/${sessionId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setUploadCount(data.count || uploadCount + 1);
      setLastSuccess(true);
      setTimeout(() => setLastSuccess(false), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : "Erreur lors de l'envoi");
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      Array.from(files).forEach(uploadPhoto);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-6"
      style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)' }}
      data-testid="capture-page"
    >
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl p-6 space-y-5">
        {/* Header */}
        <div className="text-center">
          <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-[#6366f1] to-[#8b5cf6] rounded-2xl flex items-center justify-center shadow-lg">
            <Camera className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-lg font-bold text-[#1e293b] font-['Outfit']">MediPocket</h1>
          <p className="text-xs text-[#64748b] mt-1">
            Session <span className="font-mono font-medium text-[#6366f1]">{sessionId}</span>
          </p>
        </div>

        {/* Mode toggle */}
        <div className="flex bg-slate-100 rounded-xl p-1" data-testid="mode-toggle">
          <button
            onClick={() => setScanMode(false)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
              !scanMode ? 'bg-white text-[#6366f1] shadow-sm' : 'text-[#64748b]'
            }`}
            data-testid="mode-photo"
          >
            <Camera className="w-4 h-4 inline mr-1.5" />
            Photo
          </button>
          <button
            onClick={() => setScanMode(true)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
              scanMode ? 'bg-white text-[#6366f1] shadow-sm' : 'text-[#64748b]'
            }`}
            data-testid="mode-document"
          >
            <FileText className="w-4 h-4 inline mr-1.5" />
            Document
          </button>
        </div>

        {/* Upload count */}
        {uploadCount > 0 && !scanMode && (
          <div className="text-center py-1.5 px-3 bg-green-50 rounded-xl" data-testid="upload-count">
            <span className="text-green-700 text-sm font-medium">
              {uploadCount} photo{uploadCount > 1 ? 's' : ''} envoyée{uploadCount > 1 ? 's' : ''}
            </span>
          </div>
        )}

        {/* Photo Mode */}
        {!scanMode && (
          <>
            {/* Camera input (with capture attribute → opens camera) */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleFileChange}
              data-testid="capture-input"
            />

            {/* Gallery input (NO capture attribute → opens photo picker) */}
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleFileChange}
              data-testid="gallery-input"
            />

            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] hover:from-[#4f46e5] hover:to-[#7c3aed] text-white font-medium text-base shadow-lg shadow-[#6366f1]/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="btn-capture"
            >
              {uploading ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Envoi...</>
              ) : (
                <><Camera className="w-5 h-5" /> Prendre une photo</>
              )}
            </button>

            <button
              onClick={() => galleryInputRef.current?.click()}
              disabled={uploading}
              className="w-full py-3 rounded-2xl border-2 border-dashed border-[#6366f1]/30 text-[#6366f1] font-medium hover:bg-[#6366f1]/5 transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
              data-testid="btn-gallery"
            >
              <Upload className="w-4 h-4" /> Depuis la galerie
            </button>
          </>
        )}

        {/* Document Scan Mode */}
        {scanMode && (
          <DocumentScanner sessionId={sessionId} onError={setError} />
        )}

        {/* Success indicator */}
        {lastSuccess && !scanMode && (
          <div className="flex items-center justify-center gap-2 text-green-600" data-testid="upload-success">
            <Check className="w-5 h-5" />
            <span className="text-sm font-medium">Envoyée !</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="capture-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <p className="text-center text-[10px] text-[#94a3b8]">
          {scanMode
            ? 'Photographiez chaque page, puis créez le PDF (max 6 pages).'
            : 'Les photos sont envoyées directement sur votre PC.'
          }
          <br />Session valide 15 minutes.
        </p>
      </div>
    </div>
  );
}
